import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  HStack,
  KdsButton,
  KdsDivider,
  KdsText,
  VStack,
} from "@kazaar-platform/design-system";
import { RiArrowRightLine, RiDeleteBin3Line } from "@remixicon/react";
import { T, useTranslate } from "@tolgee/react";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { AttributeDtoTypeEnum } from "@/config/apiClient";
import { FieldValueSource, useOpenBrief } from "@/hooks";
import { ModalAddBriefToOrder } from "@/modals/openBrief/ModalAddBriefToOrder";
import { ModalCancelBrief } from "@/modals/openBrief/ModalCancelBrief/ModalCancelBrief";
import { BriefSummaryContextForm } from "./_partials/BriefSummaryContextForm";
import { BriefSummaryFieldsForm } from "./_partials/BriefSummaryFieldsForm";
import {
  type OpenBriefFormType,
  openBriefFormSchema,
} from "./BriefSummary.schema";
import { type BriefSummaryProps } from "./BriefSummary.types";

export const BriefSummary = ({
  initialAIValues,
  onFieldsChange,
  onReset,
}: BriefSummaryProps) => {
  const [isModalCancelBriefOpen, setIsModalCancelBriefOpen] = useState(false);
  const [isModalCreateBriefOpen, setIsModalCreateBriefOpen] = useState(false);

  const { t } = useTranslate();
  const formMethods = useForm<OpenBriefFormType>({
    resolver: zodResolver(openBriefFormSchema),
    defaultValues: {
      productId: initialAIValues?.productId || undefined,
      deliverableId: initialAIValues?.deliverableId || undefined,
      quantity: (initialAIValues?.quantity || 1).toString(),
      versionCount: (initialAIValues?.versionCount || 1).toString(),
      fields: {},
      initialExpression: initialAIValues?.initialExpression || "",
    },
  });

  const fields = formMethods.watch("fields");
  const productId = formMethods.watch("productId");

  const { useGetAttributes, useGetReferentialProducts } = useOpenBrief();
  const { data: referentialProducts } = useGetReferentialProducts();
  const { data: productAttributes } = useGetAttributes(Number(productId) || 0);

  // Sync initialAIValues to form when they change (coming from the IA)
  useEffect(() => {
    if (!initialAIValues) {
      return;
    }

    formMethods.reset(
      {
        ...formMethods.getValues(),
        productId: initialAIValues.productId,
        deliverableId: initialAIValues.deliverableId,
        quantity: (initialAIValues.quantity || 1).toString(),
        versionCount: (initialAIValues.versionCount || 1).toString(),
        initialExpression: initialAIValues.initialExpression || "",
      },
      { keepDirtyValues: false }
    );
  }, [initialAIValues, formMethods]);

  // Sync dynamic fields (attributes) to form
  useEffect(() => {
    const attributes = productAttributes?.data?.attributes;
    if (!attributes) {
      return;
    }

    const currentFields = formMethods.getValues("fields");
    const newFields: OpenBriefFormType["fields"] = {};

    attributes.forEach((attr) => {
      const id = Number(attr.idAdma);
      const aiFieldValue = initialAIValues?.fields?.[id]?.value;

      const rawValue =
        aiFieldValue !== undefined
          ? aiFieldValue
          : currentFields[id]?.value || "";

      let finalValue = rawValue;

      if (
        attr.type === AttributeDtoTypeEnum.Numerical ||
        attr.type === AttributeDtoTypeEnum.Decimal
      ) {
        const parsed =
          rawValue !== "" && rawValue !== null ? Number(rawValue) : Number.NaN;
        finalValue = !Number.isNaN(parsed) ? String(parsed) : "";
      }

      newFields[id] = {
        id,
        label: attr.label,
        value: finalValue,
        type: attr.type,
      };
    });

    formMethods.setValue("fields", newFields, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [productAttributes, initialAIValues, formMethods]);

  // Notify parent when fields change
  useEffect(() => {
    const fieldsArray = Object.keys(fields).map((key) => {
      const field = fields[Number(key)];

      return {
        id: key,
        label:
          productAttributes?.data.attributes.find((f) => f.idAdma === key)
            ?.label || "",
        value: field.value,
        idAdma: `${field.id}`,
        source: FieldValueSource.ASSISTANT,
        type: field.type,
      };
    });

    onFieldsChange(fieldsArray);
  }, [fields, productAttributes, onFieldsChange]);

  const handleReset = () => {
    formMethods.reset({
      productId: undefined,
      deliverableId: undefined,
      fields: {},
      initialExpression: "",
    });

    onReset?.();
    setIsModalCancelBriefOpen(false);
  };

  return (
    <Box
      bgColor="elevation.first"
      borderLeftColor="outlined.border"
      borderWidth="bw-small"
      borderStyle="solid"
      w="100%"
      h="100%"
    >
      <VStack gap="sp-md" justify="space-between" h="100%">
        <Box grow={1} minH="0" px="sp-md" pt="sp-xl" overflowY="auto">
          <FormProvider {...formMethods}>
            <form
              id="brief-summary-from"
              onSubmit={formMethods.handleSubmit(() =>
                setIsModalCreateBriefOpen(true)
              )}
              noValidate
            >
              <VStack gap="sp-md">
                <VStack>
                  <KdsText size="title-md">
                    {t("openBrief.briefSummaryTitle")}
                  </KdsText>
                  <KdsText size="body-sm" color="text-bodyvariant">
                    <T
                      keyName="openBrief.briefSummaryDetails"
                      params={{ b: <b /> }}
                    />
                  </KdsText>
                </VStack>

                <VStack gap="sp-md">
                  <BriefSummaryContextForm
                    initialAIValues={initialAIValues}
                    productAttributes={productAttributes?.data}
                    referentialProducts={referentialProducts?.data}
                  />

                  <KdsDivider />

                  {productAttributes?.data && (
                    <BriefSummaryFieldsForm
                      initialAIValues={initialAIValues}
                      productAttributes={productAttributes?.data}
                    />
                  )}
                </VStack>
              </VStack>
            </form>
          </FormProvider>
        </Box>

        <Box
          grow={0}
          shrink={0}
          basis="0"
          borderTopColor="outlined.border"
          borderWidth="bw-small"
          borderStyle="solid"
          pb="sp-xl"
          pt="sp-md"
          px="sp-md"
        >
          <HStack justify="space-between" gap="sp-md">
            <KdsButton
              variant="ghost"
              color="error"
              onPress={() => setIsModalCancelBriefOpen(true)}
              iconLeft={RiDeleteBin3Line}
            >
              {t("openBrief.deleteBrief")}
            </KdsButton>

            <KdsButton
              type="submit"
              form="brief-summary-from"
              iconRight={RiArrowRightLine}
            >
              {t("openBrief.addToOrder")}
            </KdsButton>
          </HStack>
        </Box>

        <ModalAddBriefToOrder
          values={formMethods.getValues()}
          isOpen={isModalCreateBriefOpen}
          onOpenChange={() => setIsModalCreateBriefOpen(false)}
          initialAIValues={initialAIValues}
        />

        <ModalCancelBrief
          isOpen={isModalCancelBriefOpen}
          onOpenChange={() => setIsModalCancelBriefOpen(false)}
          onReset={handleReset}
        />
      </VStack>
    </Box>
  );
};
